import React from "react";
import { useTranslation } from "react-i18next";
import {
	CheckboxListSection,
	Section,
	toggleInGroup,
} from "@/components/Shared/Filters/filterControls";
import Checkbox from "../../../components/Core/Checkbox/Checkbox";
import CompetenceCheckboxTree from "../../../components/Shared/CompetenceCheckboxTree/CompetenceCheckboxTree";
import { GENDER_TYPE } from "../../../configs/constants/GenderType";
import { CompetenceItem } from "../../../models/Competence";
import {
	ACTUAL_AVAILABILITY_DAYS,
	ActualAvailabilityDay,
	BookingState,
	CompetenceSelection,
	FilterDefinition,
	getActualAvailability,
	WEEKDAYS,
} from "../../../models/ConsultantFilter";
import { NO_SUBSIDIARY } from "../../../models/Subsidiary";
import { cn } from "../../../utils/cn";

export interface FilterOptions {
	genders: GENDER_TYPE[];
	/** `active: false` = deactivated (or removed) account; hidden behind a link */
	managers: { id: string; name: string; active: boolean }[];
	/** home towns present in the list: canonical key + display spelling */
	cities: { key: string; name: string }[];
	/** org `certificationTypes` (key + resolved Swedish name) */
	certificationTypes: { key: string; name: string }[];
	/** org `consultantTagTypes` (key + resolved Swedish name) */
	consultantTagTypes: { key: string; name: string }[];
	/** the org's subsidiaries; the section is hidden when the org has none */
	subsidiaries: { id: string; name: string }[];
}

interface Props {
	definition: FilterDefinition;
	onChange: (def: FilterDefinition) => void;
	options: FilterOptions;
	/** the org competence tree (branch → service → skills) for the picker */
	competenceTree: CompetenceItem[];
}

/**
 * The consultant set of filter controls (status, competences, gender,
 * manager, org units). Reused by the consultant-list filter drawer and the
 * org-defined-filters settings editor.
 */
const FilterBuilder: React.FC<Props> = ({ definition, onChange, options, competenceTree }) => {
	const { t } = useTranslation();

	const setCompetenceSelections = (selections: CompetenceSelection[]) =>
		onChange({ ...definition, competenceSelections: selections.length ? selections : undefined });

	// Always the record shape; saved/org filters may still carry the legacy array.
	const actualAvailability = getActualAvailability(definition);
	const setBookingState = (day: ActualAvailabilityDay, state: BookingState | undefined) => {
		const next = { ...actualAvailability };
		if (state) next[day] = state;
		else delete next[day];
		onChange({
			...definition,
			actualAvailability: Object.keys(next).length ? next : undefined,
		});
	};

	return (
		<>
			<Section title={t("STATUS")} count={definition.status ? 1 : 0}>
				<Checkbox
					customStyle
					labelClassName="text-base"
					label={t("ACTIVE")}
					isChecked={definition.status === "active"}
					onChange={() =>
						onChange({
							...definition,
							status: definition.status === "active" ? undefined : "active",
						})
					}
				/>
				<Checkbox
					customStyle
					labelClassName="text-base"
					label={t("INACTIVE")}
					isChecked={definition.status === "inactive"}
					onChange={() =>
						onChange({
							...definition,
							status: definition.status === "inactive" ? undefined : "inactive",
						})
					}
				/>
			</Section>

			{!!competenceTree.length && (
				<Section
					title={t("COMPETENCES" as any)}
					divider
					count={definition.competenceSelections?.length ?? 0}
				>
					<div className="flex justify-start">
						<div className="flex overflow-hidden rounded-sm border border-solid border-gray-300 text-xs">
							{(["any", "all"] as const).map((mode) => {
								const active = (definition.competenceMatch ?? "any") === mode;
								return (
									<button
										key={mode}
										type="button"
										onClick={() => onChange({ ...definition, competenceMatch: mode })}
										className={cn(
											"border-0 px-2 py-1",
											active ? "bg-black text-white" : "bg-white text-gray-600"
										)}
									>
										{t((mode === "any" ? "MATCH_ANY" : "MATCH_ALL") as any)}
									</button>
								);
							})}
						</div>
					</div>
					<div className="max-h-48 overflow-auto">
						<CompetenceCheckboxTree
							competences={competenceTree}
							value={definition.competenceSelections ?? []}
							onChange={setCompetenceSelections}
							showCounts={false}
						/>
					</div>
				</Section>
			)}

			{/* Valid (unexpired) entries only — see matchesDefinition */}
			<CheckboxListSection
				title={t("Certifikat" as any)}
				divider
				options={options.certificationTypes.map((c) => ({ value: c.key, label: c.name }))}
				selected={definition.certifications}
				onToggle={(value) => onChange(toggleInGroup(definition, "certifications", value))}
			/>

			<CheckboxListSection
				title={t("Taggar" as any)}
				divider
				options={options.consultantTagTypes.map((tag) => ({ value: tag.key, label: tag.name }))}
				selected={definition.tags}
				onToggle={(value) => onChange(toggleInGroup(definition, "tags", value))}
			/>

			{/* OR within the group; NO_SUBSIDIARY matches "organisation directly". */}
			{options.subsidiaries.length > 0 && (
				<CheckboxListSection
					title={t("SUBSIDIARY")}
					divider
					options={[
						{ value: NO_SUBSIDIARY, label: t("NO_SUBSIDIARY") },
						...options.subsidiaries.map((s) => ({ value: s.id, label: s.name })),
					]}
					selected={definition.subsidiaryIds}
					onToggle={(value) => onChange(toggleInGroup(definition, "subsidiaryIds", value))}
				/>
			)}

			<CheckboxListSection
				title={t("GENDER" as any)}
				divider
				options={options.genders.map((gender) => ({ value: gender, label: t(gender as any) }))}
				selected={definition.genders}
				onToggle={(value) => onChange(toggleInGroup(definition, "genders", value))}
			/>

			{/* Deactivated managers still own historic consultants, so they stay
			    filterable — just behind a link instead of in the default list. */}
			<CheckboxListSection
				title={t("MANAGER" as any)}
				divider
				options={options.managers
					.filter((manager) => manager.active)
					.map((manager) => ({ value: manager.id, label: manager.name }))}
				extraOptions={options.managers
					.filter((manager) => !manager.active)
					.map((manager) => ({ value: manager.id, label: manager.name }))}
				showExtraLabel={t("SHOW_INACTIVE_MANAGERS")}
				hideExtraLabel={t("HIDE_INACTIVE_MANAGERS")}
				selected={definition.managerIds}
				onToggle={(value) => onChange(toggleInGroup(definition, "managerIds", value))}
			/>

			{/* Home town (profile.address.city); OR within the group. */}
			<CheckboxListSection
				title={t("CITY")}
				divider
				options={options.cities.map((city) => ({ value: city.key, label: city.name }))}
				selected={definition.cities}
				onToggle={(value) => onChange(toggleInGroup(definition, "cities", value))}
			/>

			{/* Matches consultants with availability on every selected day (AND). */}
			<CheckboxListSection
				title={t("STATED_AVAILABILITY")}
				divider
				options={WEEKDAYS.map((day) => ({ value: day, label: t(day.toUpperCase() as any) }))}
				selected={definition.availabilityDays}
				onToggle={(value) => onChange(toggleInGroup(definition, "availabilityDays", value))}
			/>

			{/* Actual bookings, not the consultant's stated schedule: checking a day
			    adds a requirement on it (all checked days must hold), and the toggle
			    picks which — free (no shift, no absence) or booked (busy, which
			    includes absences). Unchecked = no constraint on that day. */}
			<Section
				title={t("ACTUAL_AVAILABILITY")}
				divider
				count={Object.keys(actualAvailability).length}
			>
				{ACTUAL_AVAILABILITY_DAYS.map((day) => {
					const state = actualAvailability[day];
					return (
						<div key={day} className="flex h-7 items-center justify-between gap-2">
							<Checkbox
								customStyle
								labelClassName="text-base"
								label={t(day.toUpperCase() as any)}
								isChecked={!!state}
								onChange={() => setBookingState(day, state ? undefined : "free")}
							/>
							{state && (
								<div className="flex overflow-hidden rounded-sm border border-solid border-gray-300 text-xs">
									{(
										[
											["free", t("BOOKING_STATE_FREE")],
											["booked", t("BOOKING_STATE_BOOKED")],
										] as [BookingState, string][]
									).map(([value, label]) => (
										<button
											key={value}
											type="button"
											onClick={() => setBookingState(day, value)}
											className={cn(
												"border-0 px-2 py-1",
												state === value ? "bg-black text-white" : "bg-white text-gray-600"
											)}
										>
											{label}
										</button>
									))}
								</div>
							)}
						</div>
					);
				})}
			</Section>
		</>
	);
};

export default FilterBuilder;
